import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { Iconify } from 'src/components/iconify';

export function RHFCurrencyMultiplierSelect({
  name,
  label,
  options,
  slotProps,
  helperText,
  ...other
}) {
  const { control } = useFormContext();

  const getCurrencyIndex = (arr, key) => arr.findIndex((obj) => obj.key === key);

  const toggleCurrency = (arr, key) => {
    const index = getCurrencyIndex(arr, key);
    if (index !== -1) {
      const copy = [...arr];
      copy.splice(index, 1);
      return copy;
    }
    return [...arr, { key, value: 1 }];
  };

  const labelFor = (key) => options.find((o) => o.value === key)?.label || key;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field, fieldState: { error } }) => {
        const selectedKeys = field.value.map((item) => item.key);

        return (
          <FormControl component="fieldset" sx={{ width: '100%', ...slotProps?.wrap }}>
            {label && (
              <FormLabel
                component="legend"
                {...slotProps?.formLabel}
                sx={{ mb: 1, typography: 'body2', ...slotProps?.formLabel?.sx }}
              >
                {label}
              </FormLabel>
            )}

            {/* Pick which currencies are enabled */}
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  sm: 'repeat(4, 1fr)',
                  md: 'repeat(10, 1fr)',
                },
              }}
              {...other}
            >
              {options.map((option) => {
                const checked = selectedKeys.includes(option.value);
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    color={checked ? 'primary' : 'default'}
                    variant={checked ? 'filled' : 'outlined'}
                    onClick={() => field.onChange(toggleCurrency(field.value, option.value))}
                    icon={
                      checked ? (
                        <Iconify icon="eva:checkmark-fill" width={16} />
                      ) : (
                        <Iconify icon="mingcute:add-line" width={16} />
                      )
                    }
                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                    {...slotProps?.chip}
                  />
                );
              })}
            </Box>

            {/* Multiplier per selected currency */}
            {field.value.length > 0 && (
              <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {field.value.map((item, index) => {
                  const errMsg = Array.isArray(error) ? error[index]?.value?.message : '';
                  return (
                    <Stack
                      key={item.key}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        pl: 1.25,
                        pr: 0.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: (theme) =>
                          `solid 1px ${errMsg ? theme.palette.error.main : theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ width: 40, flexShrink: 0 }}>
                        {labelFor(item.key)}
                      </Typography>
                      <TextField
                        hiddenLabel
                        type="number"
                        size="small"
                        variant="filled"
                        placeholder="Multiplier"
                        error={!!errMsg}
                        value={item.value ?? ''}
                        onChange={(e) => {
                          const newValue = [...field.value];
                          newValue[index] = {
                            key: item.key,
                            value: Number(e.target.value) ? Number(e.target.value) : undefined,
                          };
                          field.onChange(newValue);
                        }}
                        slotProps={{ htmlInput: { onWheel: (e) => e.target.blur() } }}
                        sx={{ width: '15ch', '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => field.onChange(toggleCurrency(field.value, item.key))}
                        >
                          <Iconify icon="mingcute:close-line" width={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                })}
              </Box>
            )}

            {(!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ mx: 0, mt: 1 }} {...slotProps?.formHelperText}>
                {error && !Array.isArray(error) ? error?.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
